import { isBrowser } from '@utils/isBrowser';
import classNames from 'classnames';
import { RefObject, useCallback, useEffect, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import Portal from '../../../components/Portal';

type Position = 'above' | 'below';
type Props = {
  selectionRef: RefObject<HTMLElement>;
  parentRef: RefObject<HTMLElement>;
  onShow?: () => void;
  onHide?: () => void;
  children?: React.ReactNode;
  id?: string;
};

const MAX_DISTANCE_FROM_TOP = 200;
const OFFSET = 10;

export const Tooltip: React.FC<Props> = ({
  children,
  id,
  onHide,
  onShow,
  parentRef,
  selectionRef,
}) => {
  const [tooltip, setTooltip] = useState<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>('above');
  const [coordinates, setCoordinates] = useState({ left: 0, top: 0 });

  const hide = useCallback(() => {
    if (onHide) onHide();

    setVisible(false);
  }, [onHide]);

  const show = useCallback(() => {
    if (onShow) onShow();
    if (selectionRef.current === null) return;

    setVisible(true);
    setPosition(
      selectionRef.current.getBoundingClientRect().top > MAX_DISTANCE_FROM_TOP
        ? 'above'
        : 'below',
    );
  }, [onShow, selectionRef]);

  useEffect(() => {
    if (selectionRef.current === null) return;

    selectionRef.current.addEventListener('mouseenter', show);
    selectionRef.current.addEventListener('mouseleave', hide);
    selectionRef.current.addEventListener('click', show);
    selectionRef.current.addEventListener('blur', hide);
    selectionRef.current.addEventListener('focus', show);
    window.addEventListener('scroll', hide, { passive: true });

    return () => {
      selectionRef.current?.removeEventListener('mouseenter', show);
      selectionRef.current?.removeEventListener('mouseleave', hide);
      selectionRef.current?.removeEventListener('click', show);
      selectionRef.current?.removeEventListener('blur', hide);
      selectionRef.current?.removeEventListener('focus', show);
      window.removeEventListener('scroll', hide);
    };
  }, [selectionRef, show, hide]);

  useIsomorphicLayoutEffect(() => {
    if (tooltip === null || parentRef.current === null) return;

    const parentRect = parentRef.current.getBoundingClientRect();
    const left =
      parentRect.left - tooltip.clientWidth / 2 + parentRect.width / 2;
    const top =
      position === 'above'
        ? parentRect.top - tooltip.clientHeight - OFFSET
        : parentRect.top + parentRect.height + OFFSET;

    setCoordinates((current) => {
      return current.left === left && current.top === top
        ? current
        : { left, top };
    });
  });

  if (!isBrowser()) return null;
  if (selectionRef.current === null) return null;
  if (parentRef.current === null) return null;

  return (
    <Portal>
      <div
        ref={setTooltip}
        id={id}
        role="tooltip"
        className={classNames(
          'pointer-events-none fixed z-90',
          'bg-neutral-6 text-center',
          'max-w-44 rounded-sm px-2 py-2 transition duration-300 ease-out',
          visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        )}
        style={coordinates}
      >
        <div
          className={classNames(
            'absolute left-1/2 -ml-1 h-0 w-0 border-4 border-neutral-6',
            {
              '-bottom-1 border-b-0': position === 'above',
              '-top-1 border-t-0': position === 'below',
            },
          )}
          style={{
            borderLeftColor: 'transparent',
            borderRightColor: 'transparent',
          }}
        />
        <p className="text-xs text-white">{children}</p>
      </div>
    </Portal>
  );
};
